import { Fragment, useEffect, useMemo, useRef, useContext } from "react";
import { useSelector } from "react-redux";
import isEmpty from "lodash/isEmpty";
import dynamic from "next/dynamic";
import debounce from "lodash/debounce";
import useRouter from "@stadium/hooks/lib/use-custom-route";

import useShowNewHeader from "@hooks/use-show-new-header";

import Loaders from "@stadium/ui/src/components/loaders/loaders";
import ShimmerWrapper from "@stadium/ui/src/components/loaders/shimmer-wrapper";
import Multimedia from "@sharedComponents/multimedia";
import GlobalProductSearch from "@sharedComponents/global-search/global-product-search";

import { LISTING_SECTION_REF } from "@components/store/products/listing";

import { getCountry } from "@store/selectors/country";
import {
  getStoreCloneId,
  getStoreID,
  getStoreParentId,
  getStoreSyncedToES,
  getCommonElasticSearchStoreVariables,
  getIsBundleShop,
  getIsLockerShop,
  getIsLegacyLockerShop,
  getIsFiltersOnLeft,
  getIsSinglePageShop,
} from "@store/selectors/store";
import { getPricelessProductFetchVariables } from "@selectors/product";
import { getMenuAllChildren } from "@selectors/menu";
import { getAllMenuTaxons } from "@helpers/menu";
import { getCommonGlobalCountryVariables, getLockerShopESQueryVars } from "@helpers/store";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import { usePreviewModeContext } from "@contexts/preview-mode-context";

import Product from "@sharedComponents/cards/product";
import ProductListingHeader from "@components/store/products/listing-header";
import { ANALYTICS_TARGET_FILTER, ANALYTICS_TARGET_PAGINATION } from "@constants/analytics/filter";
import FilterWrapper, { AskAnExpertHelp } from "@stadium/shared/src/components/filter";
import ListingPagination from "@components/store/products/listing-pagination";
import ScrollToTopBtn from "@stadium/ui/src/components/scroll-to-top-button";
import Portal from "@stadium/ui/src/components/portal";
import OldSearch from "@components/store/products/listing/search/old-search";
import FilterPills from "@components/store/products/filter-pills";
import { FilterType } from "@stadium/shared/src/components/filter/types";

import useSearchProductList from "@hooks/search/use-fetch-base-product-list";
import { useStoreContext } from "@components/store/provider";
import { withSyncFeatureFlag } from "@stadium/services/launch-darkly";
import useFilterConfig from "@hooks/search/use-filter-config";
import { getFilterQueryVariables } from "@helpers/products/search";
import { useShopAnalyticsContext } from "@hooks/analytics/use-shop-analytics-context";
import { useCreateSwagContext } from "@contexts/create-swag-context";

import ShadowCard from "@sharedComponents/cards/shadow-card";
import { BackButton } from "@sharedComponents/buttons";
import ListingScrollTracker from "@components/store/products/listing-scroll-tracker";

import { useConfig } from "@stadium/hooks/lib/use-config";
import { getStadiumCatalogNumber } from "@selectors/config";
import { STICKY_HEADER_REF } from "@stadium/ui/src/components/header";
import {
  selectFilterCurrentPage,
  selectFilterPerPage,
  makeFilterSort,
} from "@stadium/redux-utils/selectors/filterSelector";
import { useFilter } from "@stadium/shared/src/hooks/use-filter";

import { SystemFilterKeys } from "@stadium/redux-utils/constants/filter";

import { LOWEST_PRICE_RANGE_FILTER } from "@stadium/shared/src/constants/stores/routes";
import {
  BRAND_FILTER_KEY,
  MENU_CATEGORY_FILTER_KEY,
  PRICE_FILTER_KEY,
  PRODUCTION_DAYS_FILTER_KEY,
  RELEVANCE_SORT_ITEM,
} from "@constants/products/listing";
import {
  MENU_PAGE_PATHNAME,
  MENU_PAGE_PATHNAME_OLD,
  PRODUCT_VIEW_PATHNAME,
  SEARCH_PAGE_PATHNAME,
  getMenuRoute,
} from "@constants/routes";
import {
  OLD_SEARCH_PAGE_PATHNAME,
  OLD_PRODUCT_VIEW_PATHNAME,
  OLD_MENU_PAGE_PATHNAME,
  OLD_MENU_PAGE_PATHNAME_WITH_SUBCATEGORY,
} from "@constants/old-routes";

import notFoundImg from "@images/svg/no-result.svg";

import { ANALYTICS_EVENT_ACTION_CHANGED } from "@stadium/services/analytics/constants/default";

import styles from "../main.module.scss";

const BrandBanner = dynamic(() => import("@sharedComponents/banner/brand"));

const ALLOWED_BACK_BUTTON_ROUTES = [
  SEARCH_PAGE_PATHNAME,
  OLD_SEARCH_PAGE_PATHNAME,
  MENU_PAGE_PATHNAME,
  MENU_PAGE_PATHNAME_OLD,
  OLD_MENU_PAGE_PATHNAME,
  OLD_MENU_PAGE_PATHNAME_WITH_SUBCATEGORY,
];
const SKIPPED_BACK_BUTTON_ROUTES = [PRODUCT_VIEW_PATHNAME, OLD_PRODUCT_VIEW_PATHNAME];

const HIDE_FILTER_KEYS = [MENU_CATEGORY_FILTER_KEY, PRODUCTION_DAYS_FILTER_KEY];
const DEFAULT_FILTER_TYPES = {
  [PRICE_FILTER_KEY]: FilterType.SINGLE_SELECT,
};

const ProductListShimmerWrapper = ({ perPage = 8 }) => {
  return (
    <ShimmerWrapper length={perPage} cardClass={styles.colCard} wrapperClass={styles.rowWrapper}>
      <ShadowCard />
    </ShimmerWrapper>
  );
};

const NoProductsView = ({ searchedFor, isSwagCreationShopFlow }) => (
  <div className={styles.noProductsView}>
    <Multimedia src={notFoundImg} type="image" width={138} height={138} alt="No results found" />
    <div className={styles.noResultFound}>
      No results found under <strong>{searchedFor}</strong>.
      <div className={styles.resetFilter}>
        {" "}
        Try resetting your Search{isSwagCreationShopFlow ? " and preferred swag colors" : ""}.
      </div>
    </div>
  </div>
);

const ProductSearchList = ({ searchQuery = "", brand, searchedFor }) => {
  const { onChange, data: filterState } = useFilter();

  const router = useRouter();
  const showNewHeader = useShowNewHeader();
  const { isSwagCreationShopFlow } = useCreateSwagContext();
  const { storeBasePath, isGlobalCountry, isNewSwagmagicHost } = useStoreContext();
  const { getStoreUpdatedAt } = usePreviewModeContext();
  const productCardClass = styles.colCard;
  const storeParentId = useSelector(getStoreParentId);
  const storeId = useSelector(getStoreID);
  const country = useSelector(getCountry);
  const syncedToEs = useSelector(getStoreSyncedToES);
  const storeCloneId = useSelector(getStoreCloneId);
  const allChildren = useSelector(getMenuAllChildren);
  const commonESVariables = useSelector(getCommonElasticSearchStoreVariables);
  const isBundleShop = useSelector(getIsBundleShop);
  const isLegacyLockerShop = useSelector(getIsLegacyLockerShop);
  const pricelessVariables = useSelector(getPricelessProductFetchVariables);

  const currentPage = useSelector(selectFilterCurrentPage);
  const currentPerPage = useSelector(selectFilterPerPage);
  const currentSortBy = useSelector(useMemo(() => makeFilterSort(RELEVANCE_SORT_ITEM.id), []));

  const shopAnalyticsContext = useShopAnalyticsContext();

  // Scroll depth tracking handled by ListingScrollTracker component

  const setPage = (pageNumber) => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    onChange(SystemFilterKeys.PAGE, { id: SystemFilterKeys.PAGE, value: pageNumber });
  };

  useEffect(() => {
    if (!searchQuery && isEmpty(brand)) router.push(storeBasePath);
  }, [searchQuery, brand, storeBasePath]);

  const { allMenuTaxonIds: taxonIds, anyMenuWithSwag } = useMemo(
    () => getAllMenuTaxons(allChildren),
    [allChildren],
  );

  // TODO: Build a query builder to reuse in search and listing
  const queryVariables = useMemo(() => {
    const commonGlobalCountryVariables = getCommonGlobalCountryVariables({
      country,
      isGlobalCountry,
      countryKeyAttr: "countryCode",
    });

    return {
      storeId: storeParentId,
      designedStoreIds: [storeId],
      taxonIds,
      page: +currentPage,
      perPage: currentPerPage,
      sortBy: currentSortBy,
      searchQuery,
      skipWarehouse: !syncedToEs && !!storeCloneId,
      withSwag: anyMenuWithSwag,
      storeUpdatedAt: getStoreUpdatedAt(),
      showVariantsAsProducts: true,
      ...(isBundleShop && { bulkAvailable: false, withOutOfStock: true, withSwag: true }),
      ...commonESVariables,
      ...commonGlobalCountryVariables,
      ...getLockerShopESQueryVars({
        isLockerShop: isLegacyLockerShop,
      }),
      ...pricelessVariables,
    };
  }, [
    storeParentId,
    storeId,
    currentPage,
    currentSortBy,
    searchQuery,
    taxonIds,
    anyMenuWithSwag,
    commonESVariables,
    currentPerPage,
    pricelessVariables,
    syncedToEs,
    storeCloneId,
    getStoreUpdatedAt,
    isBundleShop,
    country,
    isGlobalCountry,
    isLegacyLockerShop,
  ]);

  const {
    loading: isProductsLoading,
    fetchedProducts,
    totalProducts,
    getProductList,
  } = useSearchProductList();

  const debouncedGetProductList = useMemo(() => debounce(getProductList, 500), [getProductList]);

  const loading = isProductsLoading || isEmpty(filterState);

  useEffect(() => {
    if (storeParentId && taxonIds.length && !isEmpty(filterState)) {
      debouncedGetProductList({
        variables: queryVariables,
      });
      return () => {
        debouncedGetProductList.cancel();
      };
    }
  }, [storeParentId, taxonIds.length, queryVariables, filterState]);

  const renderProducts = () => {
    if ((loading && fetchedProducts.length === 0) || taxonIds.length === 0)
      return <ProductListShimmerWrapper />;
    if (isEmpty(fetchedProducts))
      return (
        <NoProductsView searchedFor={searchedFor} isSwagCreationShopFlow={isSwagCreationShopFlow} />
      );
    return (
      <ListingScrollTracker baseEventProperties={shopAnalyticsContext}>
        <article
          className={`${styles.rowWrapper} ${styles.productCardsWrapper} ${
            isNewSwagmagicHost ? styles.productCardsWrapperV2 : ""
          }`}
        >
          {fetchedProducts.map((product, index) => (
            <Fragment key={`product-${product.id}`}>
              <Product
                data={product}
                key={product.id}
                source="search"
                productTileClass="white-base-tile"
                className={productCardClass}
                imageLoadingStrategy="lazy"
                eventLocationAnalytics={{
                  track: true,
                  eventProperties: {
                    ...shopAnalyticsContext,
                    product_id: product.id,
                    product_name: product.name,
                    position_in_list: index + 1,
                  },
                }}
              />
            </Fragment>
          ))}
        </article>
      </ListingScrollTracker>
    );
  };

  return (
    <section ref={LISTING_SECTION_REF} className={styles.fullwidthBg}>
      <div
        className={`${styles.searchPageContainer} ${
          showNewHeader ? "" : styles.searchPageContainerPadding
        }`}
      >
        <ProductListingHeader
          totalProducts={totalProducts}
          currentPage={currentPage}
          showFilters={false}
          source="search"
          searchedFor={searchedFor}
          showSortDropdown={!isEmpty(fetchedProducts)}
          isFetchingProducts={loading}
        />
        <ScrollToTopBtn />
        {renderProducts()}
        <ListingPagination
          total={totalProducts}
          perPage={currentPerPage}
          currentPage={currentPage}
          onPageChange={setPage}
          className="pagination"
          eventLocationAnalytics={{
            track: true,
            target: ANALYTICS_TARGET_PAGINATION,
            action: ANALYTICS_EVENT_ACTION_CHANGED,
            eventProperties: shopAnalyticsContext,
          }}
        />
      </div>
    </section>
  );
};

const ProductSearchListWithFilters = ({ searchQuery, brand, searchedFor }) => {
  const currentPage = useSelector(selectFilterCurrentPage);
  const currentPerPage = useSelector(selectFilterPerPage);
  const currentSortBy = useSelector(useMemo(() => makeFilterSort(RELEVANCE_SORT_ITEM.id), []));
  const { onChange, data: filterState } = useFilter();
  const { isSwagCreationShopFlow } = useCreateSwagContext();
  const { isMobileDevice, isDesktopDevice } = useContext(CurrentDeviceContext);

  const router = useRouter();
  const storeParentId = useSelector(getStoreParentId);
  const storeId = useSelector(getStoreID);
  const country = useSelector(getCountry);
  const syncedToEs = useSelector(getStoreSyncedToES);
  const storeCloneId = useSelector(getStoreCloneId);
  const allChildren = useSelector(getMenuAllChildren);
  const commonESVariables = useSelector(getCommonElasticSearchStoreVariables);
  const isBundleShop = useSelector(getIsBundleShop);
  const isLockerShop = useSelector(getIsLockerShop);
  const isLegacyLockerShop = useSelector(getIsLegacyLockerShop);
  const pricelessVariables = useSelector(getPricelessProductFetchVariables);

  const { allMenuTaxonIds: taxonIds, anyMenuWithSwag } = useMemo(
    () => getAllMenuTaxons(allChildren),
    [allChildren],
  );

  // Header category label: brand name, or search query (same pattern as listing)
  const headerCategoryLabel = useMemo(() => {
    if (!isEmpty(brand) && brand?.name) return brand.name;
    return "";
  }, [brand]);

  const setPage = (pageNumber) => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    onChange(SystemFilterKeys.PAGE, { id: SystemFilterKeys.PAGE, value: pageNumber });
  };

  const {
    storeBasePath,
    isGlobalCountry,
    isPreviewCatalogMode,
    withMenu,
    isNewSwagmagicHost,
    storeNumber,
  } = useStoreContext();
  const { getStoreUpdatedAt } = usePreviewModeContext();
  const productCardClass = styles.colCard;

  const isFiltersOnLeft = useSelector(getIsFiltersOnLeft);
  const showLeftFilter =
    (isNewSwagmagicHost ? isDesktopDevice : !isMobileDevice) && isFiltersOnLeft;
  const menuRoute = getMenuRoute({ storeBasePath });

  useEffect(() => {
    if (!searchQuery && isEmpty(brand)) router.push(storeBasePath);
  }, [searchQuery, brand, storeBasePath, router]);

  // TODO: Build a query builder to reuse in search and listing
  const queryVariables = useMemo(() => {
    const commonGlobalCountryVariables = getCommonGlobalCountryVariables({
      country,
      isGlobalCountry,
      countryKeyAttr: "countryCode",
    });

    const filterVariables = getFilterQueryVariables(filterState, brand);

    const variables = {
      storeId: storeParentId,
      designedStoreIds: [storeId],
      taxonIds,
      page: +currentPage,
      perPage: currentPerPage,
      sortBy: currentSortBy,
      searchQuery,
      skipWarehouse: !syncedToEs && !!storeCloneId,
      withSwag: anyMenuWithSwag,
      storeUpdatedAt: getStoreUpdatedAt(),
      showVariantsAsProducts: true,
      ...(isBundleShop && { bulkAvailable: false, withOutOfStock: true, withSwag: true }),
      ...commonESVariables,
      ...commonGlobalCountryVariables,
      ...getLockerShopESQueryVars({
        isLockerShop: isLegacyLockerShop,
      }),
      ...pricelessVariables,
      ...filterVariables,
    };

    if (isLockerShop) {
      variables.withSwag = true;

      variables.optimizedSearch = true;
    }
    return variables;
  }, [
    brand,
    storeParentId,
    storeId,
    taxonIds,
    anyMenuWithSwag,
    currentPage,
    currentPerPage,
    currentSortBy,
    searchQuery,
    syncedToEs,
    storeCloneId,
    getStoreUpdatedAt,
    filterState,
    isBundleShop,
    commonESVariables,
    country,
    isGlobalCountry,
    isLockerShop,
    isLegacyLockerShop,
    pricelessVariables,
  ]);

  const {
    loading: isProductsLoading,
    fetchedProducts,
    totalProducts,
    getProductList,
    aggregations,
  } = useSearchProductList();

  const debouncedGetProductList = useMemo(() => debounce(getProductList, 500), [getProductList]);

  const loading = isProductsLoading || isEmpty(filterState);

  const filterKeysToHide = useMemo(
    () => (isEmpty(brand) ? HIDE_FILTER_KEYS : [...HIDE_FILTER_KEYS, BRAND_FILTER_KEY]),
    [brand],
  );

  const filterConfig = useFilterConfig({
    aggregations,
    hideFilterKeys: filterKeysToHide,
    defaultFilterTypes: DEFAULT_FILTER_TYPES,
  });

  const defaultPriceValue = useMemo(() => {
    const priceSection = filterConfig?.find((config) => config.id === PRICE_FILTER_KEY);
    return Array.isArray(priceSection?.options) ? priceSection.options[0]?.value : undefined;
  }, [filterConfig]);

  // Replace "lowest-price-range" placeholder with the actual first price range bucket
  const priceAutoSelectedRef = useRef(false);
  useEffect(() => {
    if (
      !priceAutoSelectedRef.current &&
      defaultPriceValue &&
      filterState?.[PRICE_FILTER_KEY] === LOWEST_PRICE_RANGE_FILTER
    ) {
      priceAutoSelectedRef.current = true;
      onChange(FilterType.SINGLE_SELECT, {
        id: PRICE_FILTER_KEY,
        value: defaultPriceValue,
      });
    }
  }, [defaultPriceValue, filterState, onChange]);

  useEffect(() => {
    if (storeParentId && taxonIds.length && !isEmpty(filterState)) {
      debouncedGetProductList({
        variables: queryVariables,
      });
      return () => {
        debouncedGetProductList.cancel();
      };
    }
  }, [storeParentId, taxonIds.length, queryVariables, filterState]);

  const showNewHeader = useShowNewHeader();

  const shopAnalyticsContext = useShopAnalyticsContext();

  // Scroll depth tracking handled by ListingScrollTracker component

  const isSinglePageShop = useSelector(getIsSinglePageShop);
  const catalogShopNumber = useConfig(getStadiumCatalogNumber);
  const showMenu =
    storeNumber && withMenu && catalogShopNumber !== storeNumber && !isSinglePageShop;

  const renderSideBar = () => (
    <div className={`${styles.colLeftSideBar} ${styles.colLeftSideBarV2}`}>
      <div className={`${styles.leftSideBar} ${showMenu ? "" : styles.withoutMenu} `}>
        <FilterWrapper
          config={filterConfig}
          eventLocationAnalytics={{
            track: !!storeNumber,
            target: ANALYTICS_TARGET_FILTER,
            eventProperties: shopAnalyticsContext,
          }}
        />
      </div>
    </div>
  );

  const renderProducts = () => {
    if (loading || taxonIds.length === 0)
      return <ProductListShimmerWrapper perPage={filterState?.[SystemFilterKeys.PER_PAGE]} />;
    if (isEmpty(fetchedProducts))
      return (
        <NoProductsView searchedFor={searchedFor} isSwagCreationShopFlow={isSwagCreationShopFlow} />
      );
    return (
      <ListingScrollTracker baseEventProperties={shopAnalyticsContext}>
        <article
          className={`${styles.rowWrapper} ${styles.productCardsWrapper} ${
            isNewSwagmagicHost ? styles.productCardsWrapperV2 : ""
          }`}
        >
          {fetchedProducts.map((product, index) => (
            <Fragment key={`product-${product.id}`}>
              <Product
                data={product}
                key={product.id}
                source="search"
                productTileClass="white-base-tile"
                className={productCardClass}
                imageLoadingStrategy="lazy"
                eventLocationAnalytics={{
                  track: true,
                  eventProperties: {
                    ...shopAnalyticsContext,
                    product_id: product.id,
                    product_name: product.name,
                    position_in_list: index + 1,
                  },
                }}
              />
            </Fragment>
          ))}
        </article>
      </ListingScrollTracker>
    );
  };

  const renderProductsContainer = () => {
    const className = showLeftFilter ? styles["colRightSideBar"] : styles["colWithoutLeftSidebar"];
    return (
      <article
        className={`${className} ${styles.searchProductsContainer} ${
          showNewHeader ? "" : styles.searchTopPadding
        }`}
      >
        {!isDesktopDevice && !showNewHeader && (
          <BackButton
            allowedRoutes={ALLOWED_BACK_BUTTON_ROUTES}
            skipRoutes={SKIPPED_BACK_BUTTON_ROUTES}
            appendQueryParams
            fallbackRoute={menuRoute}
          >
            BACK TO MENU
          </BackButton>
        )}
        <div className={styles.listWrapper}>
          {isMobileDevice && isPreviewCatalogMode && showNewHeader && (
            <GlobalProductSearch isCatalogSearchPage />
          )}
          {!isEmpty(brand) && !showNewHeader && <BrandBanner brand={brand} />}
          <section className={styles.pageSection}>
            <ProductListingHeader
              totalProducts={totalProducts}
              currentPage={currentPage}
              showFilters={!showLeftFilter}
              source="search"
              searchedFor={isEmpty(brand) ? searchedFor : ""}
              showSortDropdown={!isEmpty(fetchedProducts)}
              title={isMobileDevice ? "" : brand?.name}
              categoryLabel={headerCategoryLabel}
              isFetchingProducts={loading}
              filterConfig={showLeftFilter ? undefined : filterConfig}
            />
            <ScrollToTopBtn />
            {!isMobileDevice && <FilterPills config={filterConfig} />}
            {renderProducts()}
            {!loading && (
              <ListingPagination
                total={totalProducts}
                perPage={currentPerPage}
                currentPage={currentPage}
                onPageChange={setPage}
                className="pagination"
                eventLocationAnalytics={{
                  track: true,
                  target: ANALYTICS_TARGET_PAGINATION,
                  action: ANALYTICS_EVENT_ACTION_CHANGED,
                  eventProperties: shopAnalyticsContext,
                }}
              />
            )}
          </section>
        </div>
      </article>
    );
  };

  return (
    <section
      className={`${styles.fullwidthBg} ${styles.onlyListingPage}`}
      ref={LISTING_SECTION_REF}
      style={{ "--sticky-header-height": `${STICKY_HEADER_REF.current?.clientHeight}px` }}
    >
      {isMobileDevice && showNewHeader && !isPreviewCatalogMode && brand?.name && (
        <Portal id="brand-page-header-container" dataContainerId="brand-page-header">
          <div
            className={styles.responsiveSubCategorySection}
            style={{ "--sticky-header-height": `${STICKY_HEADER_REF.current?.clientHeight}px` }}
          >
            <h2 className={styles.pageListTitle}>{brand?.name}</h2>
          </div>
          <div
            className={styles.responsiveBrandTitleSpacing}
            style={{ "--sticky-header-height": `${STICKY_HEADER_REF.current?.clientHeight}px` }}
          />
        </Portal>
      )}
      <div className={styles.storePageContainer}>
        <div className={`${styles.rowWrapper} ${styles.rowWrapperMain} ${styles.rowWrapperMainSW}`}>
          {showLeftFilter && (
            <div className={`${styles.listingFilterSection} ${styles.listingFilterSectionSearch}`}>
              {isLockerShop && isDesktopDevice && <AskAnExpertHelp />}
              {renderSideBar()}
            </div>
          )}
          {renderProductsContainer()}
        </div>
      </div>
    </section>
  );
};

const WrappedSearch = (props) => {
  const isBundleShop = useSelector(getIsBundleShop);
  const { init: initFilter, clear: clearFilters } = useFilter();

  useEffect(() => {
    initFilter();
    return () => clearFilters([]);
  }, []);

  if (isBundleShop) {
    return (
      <ProductSearchList
        brand={props?.brand}
        searchedFor={props?.searchedFor}
        searchQuery={props?.searchQuery}
      />
    );
  }
  return (
    <ProductSearchListWithFilters
      brand={props?.brand}
      searchedFor={props?.searchedFor}
      searchQuery={props?.searchQuery}
    />
  );
};

const ProductSearch = (props) => {
  const { isNewSwagmagicHost } = useStoreContext();

  return isNewSwagmagicHost ? <WrappedSearch {...props} /> : <OldSearch {...props} />;
};

export default withSyncFeatureFlag(ProductSearch, {
  loader: (
    <Loaders.PageLoader wrapperClass={`${styles.pageLoaderWrapper} pageLoaderwrap min-h-100`} />
  ),
});
